// @flow
import {all, call, put, select, takeEvery} from 'redux-saga/effects';
import {tripica} from '../../api';
import TrpcLibrary from 'digicel-kaizen-library/lib/tripica-library';
import type {
  ActivationBonusActionType,
  ReferralBonusesActionType,
  GodfatherBonusActionType,
} from './actions';
import {setLoading} from '../LoadingStatus';
import {
  setActivationBonus,
  setReferralBonuses,
  setGodfatherBonus,
  setFreeTrialActivationBonus,
} from './actions';
import HandleErrorService from '../../lib/handleError';
import { isLoggedInSelector } from '../Authentication';
import WizzeeLog from '../../lib/wizzeeLog';

export function* fetchReferralBonusesSaga(
  action: ReferralBonusesActionType,
): Generator<*, *, *> {
  if (action.type !== 'FETCH_REFERRAL_BONUSES') return; // for flow
  yield put(setLoading('fetchReferralBonuses', true));
  let error = false;
  try {
    const referralBonuses = yield call([tripica, 'getReferralBonuses']);
    if (referralBonuses) {
      yield put(setReferralBonuses(referralBonuses));
    }
  } catch (e) {
    WizzeeLog.warn('[saga] fetchReferralBonuses', e);
    HandleErrorService.showToastError(e);
    error = true;
  } finally {
    yield put(setLoading('fetchReferralBonuses', false));
    return {error};
  }
}

export function* fetchFreeTrialActivationBonusSaga(
  action: ActivationBonusActionType,
): Generator<*, *, *> {
  if (action.type !== 'FETCH_FREE_TRIAL_ACTIVATION_BONUS') return; // for flow
  yield put(setLoading('fetchFreeTrialActivationBonus', true));
  let error = false;
  try {
    yield put(setFreeTrialActivationBonus(null)); // reset first
    const catalog = yield call([tripica, 'getCatalog']);
    const activationBonusProductOffering = catalog.allBonus.find(
      productOffering =>
        productOffering.name === TrpcLibrary.getActivationBonusProductName(),
    );

    if (activationBonusProductOffering) {      
      const isLoggedIn = yield select(isLoggedInSelector);
      if (isLoggedIn) {        
        const response = yield call(
          [tripica, 'isActivationBonusEligible'],
          catalog,
          );
          if (response) {
            yield put(setFreeTrialActivationBonus(activationBonusProductOffering));
          }
      } else {
        yield put(setFreeTrialActivationBonus(activationBonusProductOffering));        
      }      
    }    
  } catch (e) {
    WizzeeLog.warn('[saga] fetchFreeTrialActivationBonus', e);
    HandleErrorService.showToastError(e);
    error = true;
  } finally {
    yield put(setLoading('fetchFreeTrialActivationBonus', false));
    return {error};
  }
}

export function* fetchActivationBonusSaga(
  action: ActivationBonusActionType,
): Generator<*, *, *> {
  if (action.type !== 'FETCH_ACTIVATION_BONUS') return; // for flow
  yield put(setLoading('fetchActivationBonus', true));
  let error = false;
  try {
    const activationBonus = yield call([tripica, 'getActivationBonus']);
    yield put(setActivationBonus(activationBonus));
  } catch (e) {
    WizzeeLog.warn('[saga] fetchActivationBonus', e);
    HandleErrorService.showToastError(e);
    error = true;
  } finally {
    yield put(setLoading('fetchActivationBonus', false));
    return {error};
  }
}

export function* fetchGodfatherBonusSaga(
  action: GodfatherBonusActionType,
): Generator<*, *, *> {
  if (action.type !== 'FETCH_GODFATHER_BONUS') return; // for flow
  yield put(setLoading('fetchGodfatherBonus', true));
  let error = false;
  try {
    const godfatherBonus = yield call([tripica, 'getGodfatherBonus']);
    if (godfatherBonus) {
      yield put(setGodfatherBonus(godfatherBonus));
    }
  } catch (e) {
    WizzeeLog.warn('[saga] fetchGodfatherBonus', e);
    HandleErrorService.showToastError(e);
    error = true;
  } finally {
    yield put(setLoading('fetchGodfatherBonus', false));
    return {error};
  }
}

export default function* (): Generator<*, *, *> {
  yield all([
    takeEvery('FETCH_REFERRAL_BONUSES', fetchReferralBonusesSaga),
    takeEvery('FETCH_GODFATHER_BONUS', fetchGodfatherBonusSaga),
    takeEvery('FETCH_ACTIVATION_BONUS', fetchActivationBonusSaga),
    takeEvery(
      'FETCH_FREE_TRIAL_ACTIVATION_BONUS',
      fetchFreeTrialActivationBonusSaga,
    ),
  ]);
}
